import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RefListModel } from 'src/app/models/ref-list-model';
import { GenericPolluxResult } from '../../../models/generic-pollux-result';
import { TemplateModel } from '../../../models/template-model';
import { LocalizationModel } from '../models/localization-model';
import { widgetDefinitions } from '../../report-designer/widgets-definitions';
import { ReportDesignerWidgetDefinition } from '../../report-designer/models/report-designer-widget-definition';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';

/**
 * Injectable
 */
@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  /**
   * Resource for the calculation model.
   */
  private _localizationResource: string;

  /**
   * Resource for templates.
   */
  private _templateResource: string;

  /**
   * Resource for the common service.
   */
  private _commonResource: string;

  /**
   * Edit form ID
   */
  private editFormId = new BehaviorSubject<number>(null);

  /**
   * Edit form ID subject
   */
  public editFormId$ = this.editFormId.asObservable();

  /**
   * Translation overrides
   */
  private _translationOverride: any = null;

  /**
   * Constructor
   * @param ais
   * @param httpClient
   * @param navigationService
   */
  constructor(private httpClient: HttpClient, private appServiceHelper: AppServiceHelperService) {
    this._localizationResource = this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', 'dictionary');
    this._commonResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'reflist');
    this._templateResource = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'templates');
  }

  /**
   * Sets the translation overrides
   * @param override
   */
  public setTranslationOverride(override: any) {
    this._translationOverride = override;
  }

  /**
   * Gets the translation overrides
   * @returns
   */
  public getTranslationOverride() {
    return this._translationOverride;
  }

  //#region API

  /**
 * Retrieves a list of localizations.
 * @returns An observable of localizations array.
 */
  getLocalizations(): Observable<LocalizationModel[]> {
    return this.httpClient.get<LocalizationModel[]>(`${this._localizationResource}/list`);
  }

  /**
   * Retrieves a localization by ID.
   */
  getLocalization(oid: number): Observable<LocalizationModel> {
    return this.httpClient.get<LocalizationModel>(`${this._localizationResource}/single?oid=${oid}`);
  }

  /**
   * Retrieves a reference list by name..
   */
  getRefList(name: string): Observable<RefListModel[]> {
    return this.httpClient.get<RefListModel[]>(`${this._commonResource}/pollux?reflistName=${name}`);
  }

  /**
   * Updates a localization.
   */
  updateLocalization(data: LocalizationModel): Observable<any> {
    return this.httpClient.post<LocalizationModel>(`${this._localizationResource}/update`, data);
  }

  /**
   * Deletes a localization.
   */
  deleteLocalization(id: number): Observable<any> {
    return this.httpClient.delete<LocalizationModel>(`${this._localizationResource}/delete/${id}`, {});
  }

  /**
   * Retrieves templates list
   * */
  getTemplates(): Observable<TemplateModel[]> {
    return this.httpClient.get<TemplateModel[]>(`${this._templateResource}/widgetConfig`);
  }

  /**
   * Retrieves widgets list
   * @param dictionaryId Desired dictionary id
   */
  getWidgets(dictionaryId: number) {
    return this.httpClient.get<TemplateModel[]>(`${this._localizationResource}/widgets?dictionaryId=${dictionaryId}`);
  }

  /**
   * Retrieves widgets list
   * @param widgetData Widget data
   * @param dictionaryId Desired dictionary id
   */
  updateWidget(widgetData: any, dictionaryId: number): Observable<GenericPolluxResult> {
    return this.httpClient.post<any>(`${this._localizationResource}/widget?dictionaryId=${dictionaryId}`, widgetData);
  }

  /**
   * Retrieves all widget definitions
   */
  getWidgetDefinitions(): ReportDesignerWidgetDefinition[] {
    const allWidgets: ReportDesignerWidgetDefinition[] = [];

    const result = widgetDefinitions;
    for (const category of result) {
      allWidgets.push(...category.items);
    }
    return allWidgets;
  }

  /**
   * Opens a form
   * @param id
   */
  public openForm(id: number): void {
    this.editFormId.next(id);
  }

  /**
   * Closes the form
   */
  public closeForm(): void {
    this.editFormId.next(null);
  }

  /**
   * Refresh the grid
   */
  public refreshGrid(): void {
    this.editFormId.next(-1);
    setTimeout(() => {
      this.editFormId.next(null);
    });
  }

  //#endregion
}
